import { lazy, Suspense, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { loadLanguage } from './i18n';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import KairosLogo from './components/KairosLogo';
import AuthPage from './features/auth/AuthPage';
import OnboardingPage from './features/onboarding/OnboardingPage';
import { useAuth } from './lib/useAuth';
import { supabase } from './lib/supabase';
import { useIdleSignout, IDLE_RETURN_PATH_KEY } from './lib/useIdleSignout';
import { trackAppVisit, trackPageView } from './lib/analytics';
import { HelpButton } from './features/help/HelpDrawer';
import { FeedbackButton } from './features/feedback/FeedbackDrawer';
import { APP_OVERVIEW_KEY } from './features/help/appOverviewKey';
import { useKairosSub, isKairosActive, cancelKairosSubscription } from './hooks/useKairosSub';
import JournalDrawer from './features/journal/JournalDrawer';

// ── Lazy-loaded routes ───────────────────────────────────────────────────────
// Each becomes its own chunk; only fetched when the user navigates to it.
const JourneyPage          = lazy(() => import('./features/journey/JourneyPage'));
const DailyPage            = lazy(() => import('./features/daily/DailyPage'));
const DailyJourneyPage     = lazy(() => import('./features/daily/DailyJourneyPage'));
const ChooseYourOwnPage    = lazy(() => import('./features/choose/ChooseYourOwnPage'));
const LiturgicalPage       = lazy(() => import('./features/liturgical/LiturgicalPage'));
const DashboardPage        = lazy(() => import('./features/dashboard/DashboardPage'));
const StudiesPage          = lazy(() => import('./features/studies/StudiesPage'));
const PassageStudyPage     = lazy(() => import('./features/studies/PassageStudyPage'));
const TopicStudyPage       = lazy(() => import('./features/studies/TopicStudyPage'));
const AdminPage            = lazy(() => import('./features/admin/AdminPage'));
const OwnerStatsPage       = lazy(() => import('./features/admin/OwnerStatsPage'));
const GrowthGroupsLayout   = lazy(() => import('./features/growth-groups/pages/GrowthGroupsLayout'));
const GrowthGroupsIndex    = lazy(() => import('./features/growth-groups/pages/GrowthGroupsIndex'));
const MyGroups             = lazy(() => import('./features/growth-groups/pages/MyGroups'));
const BrowseGroups         = lazy(() => import('./features/growth-groups/pages/BrowseGroups'));
const RequestGroup         = lazy(() => import('./features/growth-groups/pages/RequestGroup'));
const GroupDetail          = lazy(() => import('./features/growth-groups/pages/GroupDetail'));
const GuestJoin            = lazy(() => import('./features/growth-groups/pages/GuestJoin'));
const LeaderHealthCheckin  = lazy(() => import('./features/growth-groups/pages/LeaderHealthCheckin'));
const PastorDashboard      = lazy(() => import('./features/growth-groups/pages/PastorDashboard'));
const CoordinatorRequests  = lazy(() => import('./features/growth-groups/pages/CoordinatorRequests'));
const UnconnectedMembers   = lazy(() => import('./features/growth-groups/pages/UnconnectedMembers'));
const AnnouncementsPage    = lazy(() => import('./features/growth-groups/pages/AnnouncementsPage'));
const ChurchSetupPage      = lazy(() => import('./features/growth-groups/pages/ChurchSetupPage'));
const ChurchJoinPage       = lazy(() => import('./features/growth-groups/pages/ChurchJoinPage'));
const ChurchInviteLanding  = lazy(() => import('./features/growth-groups/pages/ChurchInviteLanding'));
const CreateGroup          = lazy(() => import('./features/growth-groups/pages/CreateGroup'));
const GuestDiscoveryPage   = lazy(() => import('./features/growth-groups/pages/GuestDiscoveryPage'));
const SeriesLibraryPage    = lazy(() => import('./features/growth-groups/pages/SeriesLibraryPage'));
const DiscoveryMapPage     = lazy(() => import('./features/growth-groups/pages/DiscoveryMapPage'));
const AppOverviewPage      = lazy(() => import('./features/help/AppOverviewPage'));
const FlowMap              = lazy(() => import('./dev/FlowMap'));
const ScriptureFlowMap     = lazy(() => import('./features/help/ScriptureFlowMap'));
const GrowthGroupsFlowMap  = lazy(() => import('./features/help/GrowthGroupsFlowMap'));
const SubscribePage        = lazy(() => import('./features/subscribe/SubscribePage'));
const TermsPage            = lazy(() => import('./features/subscribe/TermsPage'));
const ResetPasswordPage    = lazy(() => import('./features/auth/ResetPasswordPage'));

function RouteFallback() {
  return (
    <div className="app-loading">
      <KairosLogo size={44} color="#6a7662" />
    </div>
  );
}

const TIER_KEY = 'kairos_tier';
const LAST_VISITED_PATH_KEY = 'kairos_last_visited_path';
const JOURNAL_CONTEXT_KEY = 'kairos_journal_context_ref';

function isResumeCandidatePath(pathname) {
  if (!pathname || pathname === '/') return false;
  if (pathname === '/welcome' || pathname === '/subscribe' || pathname === '/terms' || pathname === '/reset-password') return false;
  if (pathname === '/dev-flow' || pathname === '/scripture-map' || pathname === '/groups-map') return false;
  if (pathname.startsWith('/church-invite/') || pathname.startsWith('/join/')) return false;
  return true;
}

function getSavedResumePath() {
  try {
    const idlePath = localStorage.getItem(IDLE_RETURN_PATH_KEY);
    if (idlePath && idlePath !== '/') return idlePath;
    const lastVisitedPath = localStorage.getItem(LAST_VISITED_PATH_KEY);
    if (lastVisitedPath && lastVisitedPath !== '/') return lastVisitedPath;
  } catch {}
  return null;
}

function clearSavedResumePath() {
  try {
    localStorage.removeItem(IDLE_RETURN_PATH_KEY);
    localStorage.removeItem(LAST_VISITED_PATH_KEY);
  } catch {}
}

function getJournalContextRef() {
  try {
    return localStorage.getItem(JOURNAL_CONTEXT_KEY) || '';
  } catch {
    return '';
  }
}

function normalizeTier(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'pro') return 'pro';
  if (normalized === 'paid') return 'paid';
  if (normalized === 'scholar') return 'scholar';
  return 'free';
}

const LEADER_ROLES = new Set(['pastor', 'coordinator', 'leader']);

const isHomeJunkGroupName = (name) => {
  const rawName = String(name || '').trim();

  if (!rawName) {
    return false;
  }

  return /^rpc-auth-\d+$/i.test(rawName)
    || /^manual browser group(?:\s+\d+)?$/i.test(rawName);
};

const getHomeGroupDisplayName = (group, index) => {
  const rawName = String(group?.name || '').trim();

  if (!rawName) {
    return `Koinonia Group ${index + 1}`;
  }

  if (isHomeJunkGroupName(rawName)) {
    return `Koinonia Group ${index + 1}`;
  }

  return rawName;
};

const LANGS = [
  { id: 'en', flag: '🇬🇧', label: 'English' },
  { id: 'af', flag: '🇿🇦', label: 'Afrikaans' },
  { id: 'zu', flag: '🇿🇦', label: 'isiZulu' },
];

function HomePage({ user, profile, saveLanguage }) {
  const { t, i18n } = useTranslation();
  const { data: sub } = useKairosSub(user?.id);
  const [todayReference, setTodayReference] = useState(t('home.todaysReading'));
  const [todayPreview,   setTodayPreview]   = useState([]);
  const [todayLoading,   setTodayLoading]   = useState(true);
  const [todayError,     setTodayError]     = useState('');
  const [myGroups,       setMyGroups]       = useState([]);
  const [isLeader,       setIsLeader]       = useState(false);
  const [groupsLoading,  setGroupsLoading]  = useState(!!user);
  const visibleHomeGroups = myGroups.filter(g => !isHomeJunkGroupName(g?.name));

  // Load today's reading preview
  const loadToday = async () => {
    setTodayLoading(true);
    setTodayError('');
    try {
      const response = await axios.get('/api/daily/passage', {
        params: { language: profile?.languagePreference || i18n.language || 'en' },
      });
      const payload = response?.data || {};
      setTodayReference(payload.reference || "Today's reading");
      setTodayPreview((payload.verses || []).slice(0, 2));
    } catch {
      setTodayError("Unable to load today's reading preview.");
      setTodayReference("Today's reading");
      setTodayPreview([]);
    } finally {
      setTodayLoading(false);
    }
  };

  // Detect group membership and leadership role via Supabase
  const loadGroups = async () => {
    if (!user) { setGroupsLoading(false); return; }
    try {
      const { supabase: sb } = await import('./lib/supabase');
      const { data, error } = await sb
        .from('group_members')
        .select('role, growth_groups(id, name)')
        .eq('user_id', user.id)
        .eq('is_active', true);
      if (!error && data) {
        const groups = data.map(r => ({ role: r.role, ...r.growth_groups })).filter(g => g.id);
        setMyGroups(groups);
        setIsLeader(groups.some(g => LEADER_ROLES.has((g.role || '').toLowerCase())));
      }
    } catch { /* group tables may not be seeded yet */ }
    finally { setGroupsLoading(false); }
  };

  useEffect(() => { loadToday(); loadGroups(); }, [i18n.language, profile?.languagePreference]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Shared sub-sections ──────────────────────────────────────────────────

  const TodayCard = () => (
    <section className="home-today-card">
      <p className="home-section-label">{t('home.todaysReading')}</p>
      <p className="home-today-ref">{todayReference}</p>
      {todayLoading && <p className="home-today-meta">{t('home.loadingPreview')}</p>}
      {!todayLoading && !!todayError && <p className="home-today-meta">{todayError}</p>}
      {!todayLoading && !todayError && (
        <div className="home-today-verses">
          {todayPreview.map((verse, index) => (
            <p key={`${verse.chapter || 0}-${verse.verse || index}`} className="home-today-line">
              <span className="home-today-verse">{verse.chapter}:{verse.verse}</span>{' '}{verse.text}
            </p>
          ))}
        </div>
      )}
      <div className="home-today-actions">
        {!!todayError && (
          <button type="button" className="home-inline-button" onClick={loadToday}>{t('home.retry')}</button>
        )}
        <Link className="home-inline-link" to="/daily">{t('home.openDaily')}</Link>
      </div>
    </section>
  );

  const ReadingPaths = () => (
    <section className="home-paths">
      <p className="home-section-label">{t('home.readingPaths')}</p>
      <div className="home-path-grid">
        <Link className="home-path-card" to="/daily"><strong>{t('nav.daily')}</strong><span>{t('home.dailyPathDesc')}</span></Link>
        <Link className="home-path-card" to="/journey"><strong>{t('nav.journey')}</strong><span>{t('home.journeyPathDesc')}</span></Link>
        <Link className="home-path-card" to="/choose-your-own"><strong>{t('nav.choose')}</strong><span>{t('home.choosePathDesc')}</span></Link>
        <Link className="home-path-card" to="/lectionary"><strong>{t('nav.lectionary')}</strong><span>{t('home.lectionaryPathDesc')}</span></Link>
      </div>
      <div className="home-today-actions" style={{ marginTop: 10 }}>
        <Link className="home-inline-link" to="/scripture-map">{t('home.viewReadingMap')}</Link>
      </div>
    </section>
  );

  const GroupsSection = ({ prominent }) => (
    <section className={`home-groups-section${prominent ? ' home-groups-prominent' : ''}`}>
      <p className="home-section-label">{t('home.koinoniaGroups')}</p>
      {groupsLoading ? (
        <p className="home-today-meta">{t('home.loadingGroups')}</p>
      ) : visibleHomeGroups.length > 0 ? (
        <>
          <div className="home-groups-list">
            {visibleHomeGroups.slice(0, 4).map((g, index) => (
              <Link key={g.id} className="home-group-card" to={`/koinonia-groups/group/${g.id}`}>
                <strong>{getHomeGroupDisplayName(g, index)}</strong>
                {g.role && <span className="home-group-role">{g.role}</span>}
              </Link>
            ))}
          </div>
          <div className="home-today-actions">
            <Link className="home-inline-link" to="/koinonia-groups">{t('home.allGroups')}</Link>
          </div>
        </>
      ) : (
        <div className="home-groups-empty">
          <p className="home-today-meta">{t('home.noGroupsYet')}</p>
          <div className="home-today-actions">
            <Link className="home-inline-link" to="/koinonia-groups">{t('home.koinoniaGroups')}</Link>
          </div>
        </div>
      )}
    </section>
  );

  const currentLang = profile?.languagePreference || 'en';
  const showTrialEndedBanner = !!user && !!sub?.trial_ends_at && !isKairosActive(sub) && new Date(sub.trial_ends_at) <= new Date();
  // Warn non-paying users that free access ends 31 May 2026
  const showFreeAccessCutoffBanner = !!user && !isKairosActive(sub) && !showTrialEndedBanner && new Date() < new Date('2026-06-01T00:00:00Z');

  const LanguageCard = () => (
    <section className="home-lang-card">
      <p className="home-section-label">{t('lang.aiLanguage')}</p>
      <div className="home-lang-options">
        {LANGS.map(l => (
          <button
            key={l.id}
            type="button"
            className={`home-lang-btn${currentLang === l.id ? ' home-lang-btn--active' : ''}`}
            onClick={() => saveLanguage && saveLanguage(l.id)}
          >
            <span className="home-lang-flag">{l.flag}</span>
            <span>{l.label}</span>
          </button>
        ))}
      </div>
    </section>
  );

  // ── Adaptive layout ──────────────────────────────────────────────────────
  return (
    <section className="home-page kairos-atmosphere">
      <div className="home-shell">
        <LanguageCard />

        {showTrialEndedBanner && (
          <section className="home-today-card">
            <p className="home-section-label">{t('paywall.trialEndedTitle', 'Your 30-day trial has ended.')}</p>
            <p className="home-today-meta">{t('paywall.trialEndedDesc', 'Subscribe to continue full access to Journey, Choose, Lectionary, and Theophilos insights.')}</p>
            <div className="home-today-actions">
              <Link className="home-inline-link" to="/subscribe">{t('paywall.trialEndedCta', 'Subscribe to continue full access')}</Link>
              <Link className="home-inline-link" to="/daily">{t('home.openDaily')}</Link>
            </div>
          </section>
        )}

        {showFreeAccessCutoffBanner && (
          <section className="home-today-card">
            <p className="home-section-label">Free access ends 31 May 2026</p>
            <p className="home-today-meta">From 1 June, a Kairos subscription is required to access Journal, Choose, Journey, Lectionary and Theophilos insights. Subscribe now — 3 days free, then R49/month.</p>
            <div className="home-today-actions">
              <Link className="home-inline-link" to="/subscribe">Subscribe now</Link>
              <Link className="home-inline-link" to="/daily">{t('home.openDaily')}</Link>
            </div>
          </section>
        )}

        <>
          <TodayCard />
          <ReadingPaths />
          <GroupsSection prominent={false} />
        </>

        <footer className="home-footer-note">{t('app.tagline')}</footer>
      </div>
    </section>
  );
}

function ProtectedKairosRoute({ children }) {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let active = true;

    async function waitForSession(maxAttempts = 6) {
      for (let i = 0; i < maxAttempts; i++) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) return session;
        await new Promise((r) => setTimeout(r, 80 * (i + 1)));
      }
      return null;
    }

    async function checkAccess() {
      const session = await waitForSession();
      if (!active) return;
      if (!session?.access_token) {
        setStatus('unauthenticated');
        return;
      }

      const fetchStatus = () =>
        axios.get('/api/auth/trial-status', {
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });

      try {
        let response;
        try {
          response = await fetchStatus();
        } catch (e1) {
          if (!active) return;
          if (e1.response?.status !== 401) throw e1;
          await new Promise((r) => setTimeout(r, 250));
          if (!active) return;
          response = await fetchStatus();
        }

        if (!active) return;
        const { emailVerified, trialActive, subscriptionActive } = response.data || {};
        setStatus(emailVerified && (trialActive || subscriptionActive) ? 'allowed' : 'denied');
      } catch (err) {
        if (!active) return;
        if (err.response?.status === 401) setStatus('unauthenticated');
        else setStatus('denied');
      }
    }

    checkAccess();

    return () => {
      active = false;
    };
  }, []);

  if (status === 'checking') {
    return (
      <div className="app-loading">
        <KairosLogo size={44} color="#6a7662" />
        <p className="app-loading-text">Checking access...</p>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/auth" replace />;
  }

  if (status === 'denied') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const { t, i18n } = useTranslation();
  const [tier, setTier] = useState(() => normalizeTier(window.localStorage.getItem(TIER_KEY) || 'free'));
  const [resumePath, setResumePath] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    loading, user, isGuest, profile,
    signIn, signUp, signOut,
    continueAsGuest, exitGuest, saveOnboarding, saveLanguage: _saveLanguage, authError,
    onboardingDone,
  } = useAuth();

  // Subscription status (show actual tier in the drawer)
  const { data: sub, isLoading: subLoading, refetch: refetchSub } = useKairosSub(user?.id);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showIosInstall, setShowIosInstall] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  // Auto sign-out after 10 minutes of inactivity (only for authenticated users, not guests)
  useIdleSignout(signOut, !!user);

  // Sync tier with actual subscription
  useEffect(() => {
    if (!user || subLoading) return;
    const actualTier = isKairosActive(sub) ? 'paid' : 'free';
    setTier(actualTier);
  }, [user, sub, subLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture PWA install prompt for the drawer menu item
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (navigator.standalone) return;
    // iOS detection: explicit UA match + iPad on iOS 13+ (reports MacIntel with touch points)
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIos(ios);
    if (ios) { setShowIosInstall(true); return; }
    const handler = (e) => { e.preventDefault(); setDeferredInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    if (outcome === 'accepted') setDeferredInstallPrompt(null);
  };

  // Wrap saveLanguage to also sync i18next UI language (locale bundle is lazy-loaded)
  const saveLanguage = (lng) => {
    if (lng && i18n.language !== lng) loadLanguage(lng).catch(() => {});
    _saveLanguage(lng);
  };

  // Sync i18next when profile language loads or changes
  useEffect(() => {
    const lng = profile?.languagePreference;
    if (lng && i18n.language !== lng) loadLanguage(lng).catch(() => {});
  }, [profile?.languagePreference]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return;
    trackAppVisit(user?.id).catch((err) => {
      console.warn('[analytics] visit tracking failed:', err?.message || err);
    });
  }, [loading, user?.id]);

  useEffect(() => {
    trackPageView(location.pathname).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    window.localStorage.setItem(TIER_KEY, normalizeTier(tier));
  }, [tier]);

  // Navigate to /welcome for first-time users
  useEffect(() => {
    if (user && onboardingDone && !localStorage.getItem(APP_OVERVIEW_KEY)) {
      navigate('/welcome');
    }
  }, [user, onboardingDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile menu on navigation
  useEffect(() => { setMenuOpen(false); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const canCancelSubscription = !!user
    && sub?.access_source === 'subscription'
    && !['free', 'canceled', 'expired'].includes(sub?.status || '');

  const openJournal = () => {
    setMenuOpen(false);
    setJournalOpen(true);
  };

  const handleCancelSubscription = async () => {
    if (!canCancelSubscription || cancelBusy) return;
    if (!window.confirm(t('nav.cancelSubscriptionConfirm'))) return;
    setCancelBusy(true);
    try {
      await cancelKairosSubscription();
      await refetchSub();
      setMenuOpen(false);
      window.alert(t('nav.cancelSubscriptionSuccess'));
    } catch (err) {
      window.alert(err?.message || t('nav.cancelSubscriptionError'));
    } finally {
      setCancelBusy(false);
    }
  };

  // Track the last meaningful in-app location so "Continue where I was"
  // can resume a prior visit, not only an idle sign-out.
  useEffect(() => {
    if (!user || !onboardingDone) return;
    if (!isResumeCandidatePath(location.pathname)) return;
    try {
      localStorage.setItem(LAST_VISITED_PATH_KEY, location.pathname + location.search);
    } catch {}
  }, [user, onboardingDone, location.pathname, location.search]);

  // Show resume prompt on home when we have either an idle-return path or a
  // saved last-visit path.
  useEffect(() => {
    if (!user || !onboardingDone) return;
    if (location.pathname !== '/' || resumePath) return;
    const savedPath = getSavedResumePath();
    if (!savedPath || savedPath === location.pathname + location.search) return;
    setResumePath(savedPath);
  }, [user, onboardingDone, location.pathname, location.search, resumePath]);

  // ── Loading splash ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="app-loading">
        <KairosLogo size={44} color="#6a7662" />
        <p className="app-loading-text">{t('app.loading')}</p>
      </div>
    );
  }

  // ── Dev tools (no auth, full-screen) ─────────────────────────────────────
  if (window.location.pathname === '/dev-flow') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/dev-flow" element={<FlowMap />} />
        </Routes>
      </Suspense>
    );
  }

  // ── Scripture map (no auth, informational full-screen overlay)
  if (window.location.pathname === '/scripture-map') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/scripture-map" element={<ScriptureFlowMap />} />
        </Routes>
      </Suspense>
    );
  }

  // ── Koinonia Groups map (auth required, full-screen overlay)
  if (user && window.location.pathname === '/groups-map') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/groups-map" element={<GrowthGroupsFlowMap />} />
        </Routes>
      </Suspense>
    );
  }

  // ── App Overview (first-impression page, full-screen overlay)
  if (user && window.location.pathname === '/welcome') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/welcome" element={<AppOverviewPage />} />
        </Routes>
      </Suspense>
    );
  }

  // ── Public routes (no auth required) ──────────────────────────────────────
  if (window.location.pathname.startsWith('/church-invite/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/church-invite/:inviteCode" element={<ChurchInviteLanding />} />
        </Routes>
      </Suspense>
    );
  }
  if (window.location.pathname.startsWith('/join/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/join/:inviteCode" element={<GuestJoin />} />
        </Routes>
      </Suspense>
    );
  }

  // ── Subscription terms (public — Visa-required disclosure page) ──────────
  if (window.location.pathname === '/terms') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
      </Suspense>
    );
  }

  // ── Password reset (public — user arrives from email link) ───────────────
  // Handles both /reset-password path AND root URL with #type=recovery hash
  // (fallback when /reset-password isn't in Supabase redirect URL allowlist)
  const _hash = window.location.hash;
  const _isRecovery =
    window.location.pathname === '/reset-password' ||
    (_hash.includes('type=recovery') && _hash.includes('access_token='));
  if (_isRecovery) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="*" element={<ResetPasswordPage />} />
        </Routes>
      </Suspense>
    );
  }

  // ── Auth gate ────────────────────────────────────────────────────────────
  if (!user && !isGuest) {
    const sp = new URLSearchParams(location.search);
    const authMode    = sp.get('mode') || 'signin';
    const authEmail   = sp.get('email') || '';
    const authNext    = sp.get('next') || '/';
    return (
      <AuthPage
        signIn={signIn}
        signUp={signUp}
        authError={authError}
        loading={loading}
        initialMode={authMode}
        initialEmail={authEmail}
        onAuth={() => {
          const savedPath = getSavedResumePath();
          clearSavedResumePath();
          if (savedPath && savedPath !== '/') {
            setResumePath(savedPath);
            navigate('/', { replace: true });
          } else {
            navigate(authNext, { replace: true });
          }
        }}
        onSignUp={() => navigate('/', { replace: true })}
        onGuest={() => continueAsGuest()}
      />
    );
  }

  // ── Onboarding ───────────────────────────────────────────────────────────
  if (!onboardingDone) {
    const handleOnboardingComplete = async (data) => {
      await saveOnboarding(data);
      navigate('/', { replace: true });
    };
    return (
      <OnboardingPage
        onComplete={handleOnboardingComplete}
        userName={user?.email || null}
      />
    );
  }

  // ── Main app ─────────────────────────────────────────────────────────────
  const subBadgeClass = !user ? '' : isKairosActive(sub) ? ((sub?.status === 'trial' || sub?.status === 'trialing') ? 'trial' : 'active') : 'free';
  const subBadgeLabel = !user || subLoading ? '' : isKairosActive(sub) ? ((sub?.status === 'trial' || sub?.status === 'trialing') ? t('tier.trial') : t('tier.active')) : t('tier.free');

  return (
    <div className="app-shell">
      {/* Backdrop overlay */}
      {menuOpen && <div className="drawer-backdrop" onClick={() => setMenuOpen(false)} />}

      {/* Left slide-out drawer */}
      <aside className={`drawer${menuOpen ? ' drawer--open' : ''}`} aria-label={t('nav.openMenu')}>
        {/* User section */}
        <div className="drawer-user">
          {user ? (
            <>
              <span className="drawer-avatar">{user.email?.[0]?.toUpperCase() ?? '?'}</span>
              <div className="drawer-user-info">
                <span className="drawer-user-name">{user.email?.split('@')[0]}</span>
                {subBadgeLabel && <span className={`drawer-tier-badge drawer-tier-badge--${subBadgeClass}`}>{subBadgeLabel}</span>}
              </div>
            </>
          ) : (
            <span className="drawer-guest-label">{t('nav.guest')}</span>
          )}
        </div>

        {/* Nav links */}
        <div className="drawer-links">
          <NavLink to="/"                end className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.home')}</NavLink>
          <NavLink to="/daily"           className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.daily')}</NavLink>
          <NavLink to="/journey"         className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.journey')}</NavLink>
          <NavLink to="/choose-your-own" className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.choose')}</NavLink>
          <NavLink to="/lectionary"      className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.lectionary')}</NavLink>
          <button className="drawer-item" onClick={openJournal}>{t('journal.title')}</button>
          <NavLink to="/koinonia-groups" className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.groups')}</NavLink>
          {profile?.is_admin && (
            <NavLink to="/admin" className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.admin')}</NavLink>
          )}
          {profile?.is_admin && (
            <NavLink to="/owner-stats" className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}>{t('nav.stats')}</NavLink>
          )}
          {canCancelSubscription && (
            <button className="drawer-item drawer-cancel-subscription" onClick={handleCancelSubscription} disabled={cancelBusy}>
              {cancelBusy ? t('subscribe.processing') : t('nav.cancelSubscription')}
            </button>
          )}
          {/* Sign out / Sign in — immediately after nav links */}
          {user ? (
            <button className="drawer-item drawer-signout" onClick={() => { setMenuOpen(false); signOut(); }}>
              {t('nav.signOut')}
            </button>
          ) : (
            <a className="drawer-item drawer-signin" href="https://app.selahtoday.org/?mode=signin" onClick={() => { exitGuest(); }}>
              {t('nav.signIn')}
            </a>
          )}
        </div>

        <div className="drawer-divider" />

        {/* Settings */}
        <div className="drawer-settings">
          <div className="drawer-setting-row">
            <span className="drawer-setting-label">{t('lang.aiLanguage')}</span>
            <select
              value={profile?.languagePreference || 'en'}
              onChange={(e) => saveLanguage(e.target.value)}
              className="drawer-lang-select"
              aria-label={t('lang.label')}
            >
              <option value="en">🇬🇧 English</option>
              <option value="af">🇿🇦 Afrikaans</option>
              <option value="zu">🇿🇦 isiZulu</option>
            </select>
          </div>

          {/* PWA install — Android/Desktop Chrome */}
          {deferredInstallPrompt && (
            <button className="drawer-item drawer-install" onClick={handleInstall}>
              📲 {t('nav.installApp')}
            </button>
          )}
          {/* PWA install — iOS: button toggles inline hint */}
          {showIosInstall && (
            <>
              <button
                className="drawer-item drawer-install"
                onClick={() => setShowIosHint(h => !h)}
              >
                📲 {t('install.iosButton')}
              </button>
              {showIosHint && (
                <p className="drawer-ios-hint drawer-ios-hint--inline">
                  {t('install.iosInlineHint')}
                </p>
              )}
            </>
          )}
          {/* Fallback: shown on non-iOS when no native prompt and not already installed */}
          {!deferredInstallPrompt && !showIosInstall && !window.matchMedia('(display-mode: standalone)').matches && !navigator.standalone && (
            <button
              className="drawer-item drawer-install"
              onClick={() => {
                if (isIos) { setShowIosHint(h => !h); return; }
                window.open(window.location.origin, '_blank');
                setMenuOpen(false);
              }}
            >
              📲 {t('nav.installApp')}
            </button>
          )}
        </div>

        <div className="drawer-spacer" />
      </aside>

      <header className="app-header">
        <nav className="app-nav">
          <button
            className="app-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={menuOpen}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
          <NavLink to="/" end className={({ isActive }) => `app-brand${isActive ? ' active' : ''}`}>
            <KairosLogo size={24} className="app-brand-logo" />
            <span>Kairos</span>
          </NavLink>
        </nav>
      </header>

      <main className="app-main">
        {/* Resume prompt after idle sign-out */}
        {resumePath && (
          <div className="resume-banner">
            <span>{t('resume.prompt')}</span>
            <button className="resume-btn resume-btn--yes" onClick={() => { const p = resumePath; clearSavedResumePath(); setResumePath(null); navigate(p, { replace: true }); }}>
              {t('resume.continue')}
            </button>
            <button className="resume-btn resume-btn--no" onClick={() => { clearSavedResumePath(); setResumePath(null); }}>
              {t('resume.startFresh')}
            </button>
          </div>
        )}
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/"                element={<HomePage user={user} profile={profile} saveLanguage={saveLanguage} />} />
          <Route path="/dashboard"       element={<ProtectedKairosRoute><DashboardPage /></ProtectedKairosRoute>} />
          <Route path="/studies"         element={<ProtectedKairosRoute><StudiesPage /></ProtectedKairosRoute>} />
          <Route path="/study/passage"   element={<ProtectedKairosRoute><PassageStudyPage /></ProtectedKairosRoute>} />
          <Route path="/study/passage/:id" element={<ProtectedKairosRoute><PassageStudyPage /></ProtectedKairosRoute>} />
          <Route path="/study/topic"     element={<ProtectedKairosRoute><TopicStudyPage /></ProtectedKairosRoute>} />
          <Route path="/study/topic/:id" element={<ProtectedKairosRoute><TopicStudyPage /></ProtectedKairosRoute>} />
          <Route path="/daily"           element={<DailyPage />} />
          <Route path="/daily-journey"   element={<DailyJourneyPage />} />
          <Route path="/journey"         element={<JourneyPage />} />
          <Route path="/choose-your-own" element={<ChooseYourOwnPage />} />
          <Route path="/lectionary"      element={<LiturgicalPage />} />
          <Route path="/liturgical"      element={<LiturgicalPage />} />
          <Route path="/admin"           element={<AdminPage />} />
          <Route path="/owner-stats"     element={<OwnerStatsPage />} />
          <Route path="/subscribe"      element={<SubscribePage />} />
          <Route path="/terms"          element={<TermsPage />} />
          <Route path="/groups" element={<Navigate replace to="/koinonia-groups" />} />
          <Route path="/koinonia-groups" element={<GrowthGroupsLayout />}>
            <Route index element={<GrowthGroupsIndex />} />
            <Route path="my-groups" element={<MyGroups />} />
            <Route path="browse" element={<BrowseGroups />} />
            <Route path="create" element={<RequestGroup />} />
            <Route path="create-direct" element={<CreateGroup />} />
            <Route path="guest-join" element={<GuestDiscoveryPage />} />
            <Route path="group/:groupId" element={<GroupDetail />} />
            <Route path="leader-health" element={<LeaderHealthCheckin />} />
            <Route path="pastor-dashboard" element={<PastorDashboard />} />
            <Route path="coordinator-requests" element={<CoordinatorRequests />} />
            <Route path="unconnected" element={<UnconnectedMembers />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
            <Route path="church-setup" element={<ChurchSetupPage />} />
            <Route path="church-join" element={<ChurchJoinPage />} />
            <Route path="series-library" element={<SeriesLibraryPage />} />
            <Route path="discover" element={<DiscoveryMapPage />} />
          </Route>
          <Route path="/join/:inviteCode" element={<GuestJoin />} />
          <Route path="/church-invite/:inviteCode" element={<ChurchInviteLanding />} />
        </Routes>
        </Suspense>
      </main>
      <JournalDrawer
        isOpen={journalOpen}
        onClose={() => setJournalOpen(false)}
        passageRef={getJournalContextRef()}
      />
      <HelpButton />
      <FeedbackButton />
    </div>
  );
}

function App() {
  // Dev tools bypass auth entirely — check before Router/AppContent render
  if (window.location.pathname === '/dev-flow') {
    return (
      <QueryClientProvider client={queryClient}>
        <Router>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/dev-flow" element={<FlowMap />} />
            </Routes>
          </Suspense>
        </Router>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppContent />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
